import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faTimes } from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS } from '../../utils/colors';
import { useTranslation } from 'react-i18next';

const SearchableSelect = ({
    options = [],
    value,
    onChange,
    name,
    placeholder: placeholderProp,
    disabled = false,
    className = '',
    error = null,
    required = false,
    autoWidth = false,
    onAddNew = null,
    onSearchChange = null,
    loading = false,
    isMulti = false,
    noOptionsMessage: noOptionsMessageProp,
    searchable = true,
    allowFreeText = false,
}) => {
    const { t } = useTranslation();
    const placeholder = placeholderProp ?? t('common.formFields.placeholders.selectDefault');
    const noOptionsMessage = noOptionsMessageProp ?? t('common.formFields.placeholders.noOptionsFound');
    const [isOpen, setIsOpen] = useState(false);
    const [openUp, setOpenUp] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [displayValue, setDisplayValue] = useState('');
    const [activeIndex, setActiveIndex] = useState(-1);
    const containerRef = useRef(null);
    const inputRef = useRef(null);
    const measureRef = useRef(null);
    const listRef = useRef(null);
    const [inputWidth, setInputWidth] = useState('100%');

    // Map options to standard format with memoization
    const normalizedOptions = useMemo(() => {
        return options.map(opt => ({
            value: opt.value,
            label: opt.label || opt.value,
            richLabel: opt.richLabel || opt.label || opt.value,
            style: opt.style || {},
            className: opt.className || ''
        }));
    }, [options]);

    // Check if an option is selected
    const isSelected = useCallback((optValue) => {
        if (isMulti) {
            return Array.isArray(value) && value.some(v => String(v) === String(optValue));
        }
        return String(value) === String(optValue);
    }, [value, isMulti]);

    // Find selected option labels for display
    useEffect(() => {
        if (isMulti) {
            if (Array.isArray(value) && value.length > 0) {
                const selectedOptions = normalizedOptions
                    .filter(opt => value.some(v => String(v) === String(opt.value)));
                
                if (selectedOptions.length === 0) {
                    setDisplayValue('');
                } else if (selectedOptions.length === 1) {
                    setDisplayValue(selectedOptions[0].label);
                } else if (selectedOptions.length === 2) {
                    setDisplayValue(`${selectedOptions[0].label}, ${selectedOptions[1].label}`);
                } else {
                    setDisplayValue(`${selectedOptions.length} selected`);
                }
            } else {
                setDisplayValue('');
            }
        } else {
            const selected = normalizedOptions.find(opt => String(opt.value) === String(value));
            if (selected) {
                setDisplayValue(selected.label);
                if (!isOpen) setSearchTerm('');
            } else {
                setDisplayValue(allowFreeText && value ? String(value) : '');
            }
        }
    }, [value, normalizedOptions, isOpen, isMulti, allowFreeText]);
    
    // Auto-width measurement
    useEffect(() => {
        if (autoWidth && measureRef.current) {
            const currentText = isOpen ? searchTerm : displayValue;
            measureRef.current.textContent = currentText || placeholder || '';
            const width = measureRef.current.offsetWidth + 64; 
            setInputWidth(`${width}px`);
        } else {
            setInputWidth('100%');
        }
    }, [autoWidth, searchTerm, displayValue, isOpen, placeholder]);
    
    // Check space below to decide if to open up or down
    useEffect(() => {
        if (isOpen && containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const dropdownHeight = 250; 
            
            if (spaceBelow < dropdownHeight && rect.top > dropdownHeight) {
                setOpenUp(true);
            } else {
                setOpenUp(false);
            }
        }
    }, [isOpen]);

    const filteredOptions = useMemo(() => {
        if (onSearchChange) return normalizedOptions;
        if (!searchTerm) return normalizedOptions;
        const lowerTerm = searchTerm.toLowerCase();
        return normalizedOptions.filter(opt =>
            String(opt.label).toLowerCase().includes(lowerTerm)
        );
    }, [normalizedOptions, searchTerm, onSearchChange]);

    const commitFreeText = useCallback((term) => {
        if (!allowFreeText || !term) return;
        const trimmed = term.trim();
        if (!trimmed) return;
        const matched = normalizedOptions.find(opt => opt.label.toLowerCase() === trimmed.toLowerCase());
        if (!matched) {
            onChange({ target: { name, value: trimmed } });
            setDisplayValue(trimmed);
        }
    }, [allowFreeText, normalizedOptions, onChange, name]);

    // Handle outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                if (isOpen) commitFreeText(searchTerm);
                setIsOpen(false);
                if (!isMulti) {
                    const selected = normalizedOptions.find(opt => String(opt.value) === String(value));
                    setDisplayValue(selected ? selected.label : (allowFreeText && value ? String(value) : ''));
                }
                setSearchTerm('');
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [value, normalizedOptions, isMulti, isOpen, searchTerm, commitFreeText, allowFreeText]);

    const onSearchChangeRef = useRef(onSearchChange);
    onSearchChangeRef.current = onSearchChange;
    useEffect(() => {
        onSearchChangeRef.current?.(searchTerm);
    }, [searchTerm]);

    useEffect(() => {
        setActiveIndex(-1);
    }, [filteredOptions.length, isOpen]);

    useEffect(() => {
        if (activeIndex >= 0 && listRef.current) {
            const items = listRef.current.querySelectorAll('li');
            if (items[activeIndex]) {
                items[activeIndex].scrollIntoView({ block: 'nearest' });
            }
        }
    }, [activeIndex]);

    const handleInputChange = (e) => {
        if (!searchable) return;
        setSearchTerm(e.target.value);
        setIsOpen(true);
    };

    const handleSelect = useCallback((option) => {
        if (isMulti) {
            const currentValues = Array.isArray(value) ? [...value] : [];
            const index = currentValues.findIndex(v => String(v) === String(option.value));
            
            let nextValues;
            if (index > -1) {
                nextValues = currentValues.filter((_, i) => i !== index);
            } else {
                nextValues = [...currentValues, option.value];
            }
            
            onChange({
                target: {
                    name: name,
                    value: nextValues
                }
            });
            // Don't close dropdown or clear search for multi-select
            setActiveIndex(-1);
        } else {
            onChange({
                target: {
                    name: name,
                    value: option.value
                }
            });
            setIsOpen(false);
            setSearchTerm('');
            setActiveIndex(-1);
            setDisplayValue(option.label);
        }
    }, [onChange, name, value, isMulti]);

    const handleKeyDown = useCallback((e) => {
        if (!isOpen) {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setIsOpen(true);
            }
            return;
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex(prev => (prev < filteredOptions.length - 1 ? prev + 1 : 0));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex(prev => (prev > 0 ? prev - 1 : filteredOptions.length - 1));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (activeIndex >= 0 && filteredOptions[activeIndex]) {
                handleSelect(filteredOptions[activeIndex]);
            }
        } else if (e.key === 'Escape') {
            commitFreeText(searchTerm);
            setIsOpen(false);
            setSearchTerm('');
            setActiveIndex(-1);
        }
    }, [isOpen, activeIndex, filteredOptions, handleSelect, searchTerm, commitFreeText]);

    const handleFocus = () => {
        if (!disabled) {
            setIsOpen(true);
        }
    };

    const clearSelection = (e) => {
        e.stopPropagation();
        onChange({
            target: {
                name: name,
                value: isMulti ? [] : ''
            }
        });
        setSearchTerm('');
        setDisplayValue('');
        if (!isMulti) setIsOpen(false);
    };

    const borderColor = error
        ? UI_COLORS.STATUS.ERROR
        : (isOpen ? UI_COLORS.BRAND.PRIMARY : UI_COLORS.FORMS.INPUT.border);

    return (
        <div 
            className={`relative ${className}`} 
            ref={containerRef}
            style={{ width: autoWidth ? 'auto' : '100%', display: autoWidth ? 'inline-block' : 'block' }}
        >
            <div
                className="relative flex items-center"
                onClick={() => !disabled && inputRef.current.focus()}
            >
                <input
                    ref={inputRef}
                    type="text"
                    name={name}
                    value={isOpen && searchable ? searchTerm : displayValue}
                    onChange={handleInputChange}
                    onFocus={handleFocus}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    readOnly={!searchable}
                    disabled={disabled}
                    className={`w-full px-4 py-3 rounded-xl border-2 outline-none transition-all duration-200 ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'} ${!searchable ? 'cursor-pointer' : ''}`}
                    style={{
                        borderColor: borderColor,
                        color: UI_COLORS.FORMS.INPUT.text,
                        width: inputWidth,
                        minWidth: autoWidth ? '120px' : 'auto'
                    }}
                    autoComplete="off"
                />
                
                {autoWidth && (
                    <span 
                        ref={measureRef} 
                        className="absolute invisible whitespace-pre px-4 py-3 border-2" 
                        style={{ font: 'inherit', letterSpacing: 'inherit' }}
                    />
                )}

                <div className="absolute right-4 flex items-center gap-2 text-gray-400">
                    {!disabled && ((isMulti && Array.isArray(value) && value.length > 0) || (!isMulti && value) || searchTerm) && (
                        <button
                            type="button"
                            onClick={clearSelection}
                            className="hover:text-gray-600 focus:outline-none"
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setIsOpen(prev => !prev); }}
                        className="focus:outline-none"
                    >
                        <FontAwesomeIcon
                            icon={faChevronDown}
                            className={`transition-transform duration-200 ${isOpen ? 'transform rotate-180' : ''}`}
                            style={{ fontSize: '0.8em' }}
                        />
                    </button>
                </div>
            </div>

            {isOpen && !disabled && (
                <div
                    className={`absolute z-50 w-full bg-white rounded-xl shadow-lg border max-h-60 overflow-y-auto ${openUp ? 'bottom-full mb-1' : 'top-full mt-1'}`}
                    style={{
                        borderColor: UI_COLORS.BORDERS.LIGHT
                    }}
                >
                    {loading ? (
                        <ul className="py-1">
                            {Array.from({ length: 4 }).map((_, i) => (
                                <li key={i} className="px-4 py-2">
                                    <div
                                        className="h-4 rounded animate-pulse"
                                        style={{
                                            backgroundColor: '#e5e7eb',
                                            width: `${60 + (i % 3) * 15}%`,
                                        }}
                                    />
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <>
                            {filteredOptions.length > 0 && (
                                <ul className="py-1" ref={listRef}>
                                    {filteredOptions.map((opt, idx) => {
                                        const selected = isSelected(opt.value);
                                        return (
                                            <li
                                                key={opt.value}
                                                onClick={() => handleSelect(opt)}
                                                className={`px-4 py-2 cursor-pointer transition-colors duration-150 flex items-center justify-between ${
                                                    idx === activeIndex
                                                        ? 'bg-teal-100 text-teal-800 font-medium'
                                                        : selected
                                                            ? 'bg-teal-50 text-teal-700 font-medium'
                                                            : 'hover:bg-gray-50 text-gray-700'
                                                } ${opt.className}`}
                                                style={opt.style}
                                            >
                                                <div className="flex-1">
                                                    {opt.richLabel}
                                                </div>
                                                {selected && (
                                                    <span className="text-teal-600 font-bold ml-2">✓</span>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                            {filteredOptions.length === 0 && !(onAddNew && searchTerm) && searchTerm && (
                                <div className="px-4 py-3 text-sm text-gray-500 text-center">
                                    {noOptionsMessage}
                                </div>
                            )}
                            {onAddNew && searchTerm && !filteredOptions.some(opt => opt.label.toLowerCase() === searchTerm.toLowerCase()) && (
                                <div
                                    className={`px-4 py-2 cursor-pointer text-blue-600 hover:bg-blue-50 flex items-center gap-2 text-sm font-medium${filteredOptions.length > 0 ? ' border-t' : ''}`}
                                    style={filteredOptions.length > 0 ? { borderColor: UI_COLORS.BORDERS.LIGHT } : {}}
                                    onClick={() => { onAddNew(searchTerm); setIsOpen(false); setSearchTerm(''); }}
                                >
                                    + Add "{searchTerm}"
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

SearchableSelect.propTypes = {
    options: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        label: PropTypes.string
    })).isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
    onChange: PropTypes.func.isRequired,
    name: PropTypes.string,
    placeholder: PropTypes.string,
    disabled: PropTypes.bool,
    className: PropTypes.string,
    error: PropTypes.any,
    required: PropTypes.bool,
    onAddNew: PropTypes.func,
    onSearchChange: PropTypes.func,
    loading: PropTypes.bool,
    noOptionsMessage: PropTypes.string,
    isMulti: PropTypes.bool,
    searchable: PropTypes.bool,
    allowFreeText: PropTypes.bool,
};

export default SearchableSelect;
